<template>
  <tr>
    <td v-if="showCheckbox">
      <w-checkbox :model="attachment.checked" />
    </td>

    <td>
      <span>{{ (useIndexAsOrder ? index + 1 : attachment.$model.order) + '.' }}</span>
    </td>

    <td>
      <div class="row">
        <div class="col-10">
          <div v-if="!attachment.$model.edit">
            <span>{{ attachment.$model.metadata.zip ? 'Paczka: ' : '' }}{{ attachment.name.$model }}</span>

            <tooltip
              v-if="
                attachment.$model.metadata &&
                  attachment.$model.metadata.signature &&
                  attachment.$model.metadata.signature.signedBy
              "
              :text="signature(attachment.$model)"
            >
              <i class="fal fa-check-circle m-1" />
            </tooltip>

            <check-permission
              v-if="attachment.$model.metadata && attachment.$model.metadata.uid"
              permission="DOCUMENT.MDOK"
              entity-type="attachment"
              :entity-id="attachment.$model.id"
            >
              <tooltip :text="uid(attachment.$model)">
                <i class="fas fa-link m-1" />
              </tooltip>
            </check-permission>

            <div class="file-description mt-1">
              {{ attachment.description.$model }}
            </div>
          </div>

          <div v-else class="form-group">
            <w-input title="Nazwa" :model="attachment.name" />

            <w-textarea class="mt-3" title="Opis" :model="attachment.description" />
          </div>
        </div>

        <div v-if="!disableOrder && editable && !useIndexAsOrder" class="col-2 text-right">
          <button
            v-if="!isFirst"
            aria-label="Przesuń plik o jedną pozycję do góry."
            class="btn btn-icon mr-2"
            @click="$emit('incrementOrder')"
          >
            <i class="fal fa-fw fa-arrow-up" />
          </button>

          <button
            v-if="!isLast"
            aria-label="Przesuń plik o jedną pozycję w dół."
            class="btn btn-icon mr-2"
            @click="$emit('decrementOrder')"
          >
            <i class="fal fa-fw fa-arrow-down" />
          </button>
        </div>
      </div>
    </td>

    <td class="text-right">
      <template v-if="isGenerated">
        <check-permission
          v-if="isSent"
          permissions="ATTACHMENT.UNSEND"
          entity-type="attachment"
          :entity-id="attachment.$model.id"
        >
          <tooltip :text="tooltipReverseSendText">
            <button
              class="btn rounded-circle btn-icon btn-light mr-2"
              :aria-label="tooltipReverseSendText"
              @click="reverseMarkAsSent()"
            >
              <i class="fal fa-fw fa-unlock" />
            </button>
          </tooltip>
        </check-permission>

        <check-permission
          v-else
          permissions="ATTACHMENT.UNSEND"
          entity-type="attachment"
          :entity-id="attachment.$model.id"
        >
          <tooltip :text="tooltipSendInfoText">
            <button class="btn btn-icon mr-2 btn-light" :aria-label="tooltipSendInfoText" @click="markAsSent()">
              <i class="fal fa-fw fa-envelope-open" />
            </button>
          </tooltip>
        </check-permission>
      </template>

      <span v-if="!attachment.$model.edit">
        <tooltip v-if="!disableDownload" :text="tooltipDownloadText">
          <post-form
            button-class="btn-icon mr-2 btn-light"
            icon="fal fa-fw fa-cloud-download"
            :aria-label-text="tooltipDownloadText"
            :url="downloadLink"
          />
        </tooltip>

        <tooltip v-if="!disableDownload && hasSubAttachment" :text="tooltipDownloadContentText">
          <post-form
            button-class="btn-icon mr-2 btn-light"
            icon="fal fa-fw fa-download"
            :aria-label-text="tooltipDownloadContentText"
            :url="downloadContentLink"
          />
        </tooltip>

        <check-permission
          v-if="!isSent && editable"
          permissions="ATTACHMENT.UPDATE"
          entity-type="attachment"
          :entity-id="attachment.$model.id"
        >
          <tooltip :text="tooltipEditText">
            <button
              class="btn rounded-circle btn-icon btn-light mr-2"
              :aria-label="tooltipEditText"
              @click="$emit('edit')"
            >
              <i class="fal fa-fw fa-pencil" />
            </button>
          </tooltip>
        </check-permission>

        <check-permission
          v-if="!isSent && editable && isGenerated"
          permissions="ATTACHMENT.UPDATE"
          entity-type="attachment"
          :entity-id="attachment.$model.id"
        >
          <tooltip :text="tooltipUpdateText">
            <button
              class="btn rounded-circle btn-icon btn-light mr-2"
              :aria-label="tooltipUpdateText"
              @click="update(attachment)"
            >
              <i class="fal fa-fw fa-paragraph" />
            </button>
          </tooltip>
        </check-permission>

        <check-permission
          v-if="!!mdokUid"
          permissions="DOCUMENT.MDOK"
          :entity-type="attachment.$model.entityType"
          :entity-id="attachment.$model.entityId"
        >
          <tooltip :text="tooltipGoToMdokText">
            <button
              class="btn rounded-circle btn-icon btn-light mr-2"
              :aria-label="tooltipGoToMdokText"
              @click="goToMdok(mdokUid)"
            >
              <i class="fas fa-link" />
            </button>
          </tooltip>
        </check-permission>

        <check-permission
          v-if="!isSent && editable && isGenerated && attachment.$model.entityType === 'document'"
          permissions="ATTACHMENT.UPDATE"
          entity-type="attachment"
          :entity-id="attachment.$model.id"
        >
          <tooltip :text="tooltipGenerateAgainText">
            <button
              class="btn btn-icon rounded-circle hover-effect-dot waves-effect waves-themed mr-2 btn-light"
              :aria-label="tooltipGenerateAgainText"
              @click="$emit('generate-again')"
            >
              <i class="fal fa-fw fa-undo" />
            </button>
          </tooltip>
        </check-permission>

        <check-permission
          v-if="editable"
          permissions="ATTACHMENT.DELETE"
          entity-type="attachment"
          :entity-id="attachment.$model.id"
        >
          <tooltip :text="tooltipRemoveText">
            <button
              class="btn btn-icon rounded-circle hover-effect-dot waves-effect waves-themed mr-2 btn-light"
              :aria-label="tooltipRemoveText"
              @click="$emit('removeAttachment')"
            >
              <i class="fal fa-fw fa-trash text-red" />
            </button>
          </tooltip>
        </check-permission>
      </span>

      <span v-else>
        <tooltip :text="tooltipSaveText">
          <button
            class="btn btn-icon rounded-circle hover-effect-dot waves-effect waves-themed mr-2"
            :aria-label="tooltipSaveText"
            @click="$emit('save', attachment)"
          >
            <i class="fal fa-fw fa-save" />
          </button>
        </tooltip>

        <tooltip :text="tooltipCancelText">
          <button
            class="btn btn-icon rounded-circle hover-effect-dot waves-effect waves-themed mr-2"
            :aria-label="tooltipCancelText"
            @click="$emit('cancel')"
          >
            <i class="fal fa-fw fa-ban text-red" />
          </button>
        </tooltip>
      </span>
    </td>
  </tr>
</template>

<script>
import { components } from '@witkac/bootstrap4-components';
import { mapState } from 'vuex';

import PostForm from '@/components/postForm/PostForm';
import Tooltip from '@/components/tooltip/Tooltip';
import { AttachmentType } from '@/enums/attachmentType.enum';
import { Errors } from '@/enums/errors.enum';
import { TableName } from '@/enums/tableName';
import { confirmModalMixin } from '@/mixins/confirmModal';
import { signatureMixin } from '@/mixins/signature';
import { documentResource } from '@/resources/document.resource';
import { config } from '@/utils/config';
import { tryParseInt } from '@/utils/number';

// @vuese
// Pojedyńczy rekord załącznika
// @group Attachment
export default {
  components: { ...components, PostForm, Tooltip },
  mixins: [signatureMixin, confirmModalMixin],
  props: {
    // Załącznik
    attachment: { type: Object, required: true },
    // Czy jest pierwszy na liście
    isFirst: { type: Boolean, required: true, default: false },
    // Czy jest ostatni na liście
    isLast: { type: Boolean, required: true, default: false },
    // Zablokuj pobieranie załącznika
    disableDownload: { type: Boolean, default: false },
    // Zablokuj zmiane kolejności
    disableOrder: { type: Boolean, default: false },
    // Czy ma być możliwość edytowania
    editable: { type: Boolean, default: true },
    // Wymuś poprawne numerowanie - blokuje możliwość zmiany kolejności
    useIndexAsOrder: { type: Boolean, required: false, default: false },
    // Kolejność w tabeli
    index: { type: Number, required: false, default: -1 },
    // Czy pokazywać checkboxy
    showCheckbox: { type: Boolean, default: false }
  },
  data() {
    return {
      config,
      tooltipCancelText: 'Anuluj',
      tooltipDownloadText: 'Pobierz',
      tooltipDownloadContentText: 'Pobierz zawartość załącznika',
      tooltipEditText: 'Edytuj',
      tooltipEditDate: 'Edytuj daty systemowe załącznika',
      tooltipGoToMdokText: 'Przejdź do MDOK',
      tooltipRemoveText: 'Usuń',
      tooltipSaveText: 'Zapisz',
      tooltipUpdateText: 'Zaktualizuj treść załącznika',
      tooltipGenerateAgainText: 'Generuj ponownie',
      tooltipSendInfoText: 'Przekazane do wysyłki',
      tooltipReverseSendText: 'Odblokuj załącznik'
    };
  },
  computed: {
    ...mapState({
      isSecretary: state => !!state.auth.nodes?.length
    }),
    downloadLink() {
      return `${config.mediaURL}/${this.attachment.$model.mediaId}`;
    },
    downloadContentLink() {
      return `${config.mediaURL}/${this.attachment.$model.metadata.content.mediaId}`;
    },
    hasSubAttachment() {
      return (
        this.attachment.$model.metadata &&
        this.attachment.$model.metadata.content &&
        this.attachment.$model.metadata.content.mediaId
      );
    },
    isGenerated() {
      return (
        this.attachment.$model.type === AttachmentType.GENERATED &&
        this.attachment.$model.metadata.edit?.editable &&
        (!!this.attachment.$model.metadata.edit.html || !!this.attachment.$model.metadata.edit.htmls)
      );
    },
    isSent() {
      return this.attachment.$model.sent === true;
    },
    editDate() {
      return (
        this.editable &&
        this.attachment.$model.type === AttachmentType.GENERATED &&
        this.attachment.$model.entityType === TableName.document &&
        this.attachment.$model.metadata?.data?.documentValues?.length
      );
    },
    mdokUid() {
      if (this.isSecretary) {
        return this.attachment.$model.metadata?.mdokJoUid;
      }
      return this.attachment.$model.metadata?.mdokUid;
    }
  },
  methods: {
    // @vuese
    // Aktualizacja HTML'a załącznika
    update() {
      this.$router.push({
        name: this.attachment.$model.entityType + 'EditGenerated',
        params: { attachmentId: this.attachment.$model.id }
      });
    },
    // @vuese
    // Zwraca numer UID z załącznika
    uid(attachment) {
      return `UID: ${attachment.metadata.uid}`;
    },
    // @vuese
    // Przechodzi do dokumentu w MDOK
    goToMdok(uid) {
      const documentId =
        this.attachment.$model.entityType === TableName.document
          ? tryParseInt(this.attachment.$model.entityId)
          : undefined;
      documentResource
        .getMdokUrlByUid(uid, documentId)
        .then(url => window.open(url, '_blank'))
        .catch(err => this.notifyError(Errors.MDOK_LINK_FAILED));
    },

    // @vuese
    // Wyświetla potwierdzenie przed wywołaniem emita
    confirmThenEmit(title, emit, sentValue) {
      this.$store.dispatch('setConfirm', {
        closeButton: 'Nie',
        submitButton: 'Tak',
        title: title,
        onSuccess: () => {
          this.attachment.$model.sent = sentValue;
          return this.$emit(emit);
        }
      });
    },

    // @vuese
    // Akcja oznaczenia jako wysłane
    markAsSent() {
      return this.confirmThenEmit('Czy na pewno zablokować możliwość edycji pisma?', 'mark-as-sent', true);
    },

    // @vuese
    // Akcja odznaczenia jako wysłane
    reverseMarkAsSent() {
      return this.confirmThenEmit('Czy na pewno odblokować możliwość edycji pisma?', 'reverse-mark-as-sent', false);
    }
  }
};
</script>

<style>
.text-red {
  color: #f00;
}
.file-description {
  font-size: 0.9em;
}
</style>
